/**
 * Round 218A Phase 8D-2 (2026-02-05) — Ambient Business Credits pill.
 *
 * Small, visually subtle display of the customer's Business Credit
 * total. Placed inline in each authenticated page's header. Reads
 * from GET /api/billing/credits and listens for the module-level
 * `paygbot:business-credits-refresh` event so the number updates
 * after every event that changes the balance (generation, refund,
 * refill, purchase, completed video attempt).
 *
 * Design contract:
 *   • Never shows per-asset credit costs.
 *   • Displays "Unlimited" for founder / internal accounts.
 *   • Fails silent — a network error returns null so the Dashboard
 *     keeps rendering.
 *   • Ambient styling matches the existing dark dashboard: slate
 *     surfaces + subtle emerald accent (matches the header brand
 *     picker + streak badge nearby).
 */
import React from 'react';
import { Coins, Plus } from '@phosphor-icons/react';
import useBusinessCredits from '@/hooks/useBusinessCredits';

const BusinessCreditsPill = ({ onAddCredits, className = '' }) => {
  const { loading, error, balance } = useBusinessCredits();

  // Fail silent — no header pill, dashboard keeps rendering.
  if (error) return null;

  const isUnlimited = Boolean(balance?.is_unlimited);
  const total = balance?.total ?? 0;

  const _formatted = () => {
    if (loading) return '';
    if (isUnlimited) return 'Unlimited';
    return total.toLocaleString('en-US');
  };

  // Hide the pill entirely during the initial fetch — avoids a
  // flash of the em-dash placeholder before useBusinessCredits
  // settles (~200ms).
  if (loading && !balance) return null;

  return (
    <div
      className={[
        'inline-flex items-center gap-2 rounded-full',
        'border border-slate-700/60 bg-slate-800/60 backdrop-blur',
        'px-3 py-1.5 text-xs sm:text-sm text-slate-200',
        'shadow-sm',
        className,
      ].join(' ')}
      data-testid="business-credits-pill"
      aria-label={
        isUnlimited
          ? 'Business Credits: Unlimited'
          : `Business Credits: ${total}`
      }
    >
      <Coins size={14} weight="fill" className="text-emerald-400" />
      <span className="hidden sm:inline text-slate-400">Credits:</span>
      <span
        className="font-semibold text-white tabular-nums"
        data-testid="business-credits-total"
      >
        {_formatted()}
      </span>
      {!isUnlimited && (
        <button
          type="button"
          onClick={onAddCredits}
          disabled={loading}
          className={[
            'ml-1 inline-flex items-center gap-1',
            'rounded-full px-2 py-0.5 text-[11px] font-medium',
            'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25',
            'transition-colors disabled:opacity-50',
          ].join(' ')}
          data-testid="business-credits-add-btn"
          aria-label="Add Business Credits"
        >
          <Plus size={11} weight="bold" />
          <span className="hidden sm:inline">Add credits</span>
          <span className="sm:hidden">Add</span>
        </button>
      )}
    </div>
  );
};

export default BusinessCreditsPill;
